import { useState } from "react";

import {
  type Exercise,
  type ProgramExercise,
  type TargetSpec,
} from "../lib/api";
import { equipmentLabel, movementLabel, muscleLabel } from "../lib/format";
import ExerciseForm from "./ExerciseForm";
import ExerciseSearch from "./ExerciseSearch";
import { useSheetDrag } from "../lib/useSheetDrag";
import { haptic, notify } from "../lib/telegram";

export interface ExercisePatch {
  exercise_id?: number;
  target_sets?: number | null;
  target_reps_min?: number | null;
  target_reps_max?: number | null;
  rep_sequence?: number[] | null;
  target_spec?: TargetSpec;
  rest_sec?: number | null;
}

/** «12-8-8» → [12, 8, 8]. Пустой ввод и мусор дают пустой список. */
const parseSequence = (value: string): number[] =>
  value
    .split(/[-–—,\s]+/)
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);

interface Props {
  item: ProgramExercise;
  onSave: (patch: ExercisePatch) => Promise<void>;
  onDelete: () => Promise<void>;
  onClose: () => void;
}

/** Типы цели в порядке частоты в программах заказчика (§2.2: метка — основной). */
const TARGET_KINDS = [
  { key: "heavy", label: "Тяжело" },
  { key: "medium", label: "Средне" },
  { key: "light", label: "Легко" },
  { key: "drop", label: "Дропсет" },
  { key: "percent_1rm", label: "% от 1ПМ" },
  { key: "rpe", label: "RPE" },
  { key: "free", label: "Свободно" },
] as const;

type TargetKind = (typeof TARGET_KINDS)[number]["key"];

const kindOf = (spec: TargetSpec): TargetKind => {
  if (spec.type === "intensity_label") return spec.value as TargetKind;
  if (spec.type === "percent_1rm") return "percent_1rm";
  if (spec.type === "rpe") return "rpe";
  return "free";
};

function specFor(kind: TargetKind, amount: string): TargetSpec {
  if (kind === "percent_1rm") return { type: "percent_1rm", percent: Number(amount) || 80 };
  if (kind === "rpe") return { type: "rpe", value: Number(amount) || 8 };
  if (kind === "free") return { type: "free" };
  return { type: "intensity_label", value: kind as "heavy" | "medium" | "light" | "drop" };
}

const num = (v: string): number | null => {
  const parsed = Number(v.replace(",", "."));
  return v.trim() === "" || Number.isNaN(parsed) ? null : parsed;
};

/**
 * Правка упражнения внутри программы.
 *
 * Одна шторка на всё: подходы, повторы, цель, отдых, замена движения и его
 * категория. Разносить это по экранам значило бы заставить листать туда-сюда
 * ради двух цифр.
 */
export default function ExerciseEditor({ item, onSave, onDelete, onClose }: Props) {
  const [sets, setSets] = useState(String(item.target_sets ?? ""));
  const [repsMin, setRepsMin] = useState(String(item.target_reps_min ?? ""));
  const [repsMax, setRepsMax] = useState(String(item.target_reps_max ?? ""));
  const [rest, setRest] = useState(String(item.rest_sec ?? ""));
  const [sequence, setSequence] = useState((item.rep_sequence ?? []).join("-"));
  const [kind, setKind] = useState<TargetKind>(kindOf(item.target_spec));
  const [amount, setAmount] = useState(
    item.target_spec.type === "percent_1rm"
      ? String(item.target_spec.percent)
      : item.target_spec.type === "rpe"
        ? String(item.target_spec.value)
        : "",
  );

  const [exercise, setExercise] = useState<Exercise>(item.exercise);
  // Поиск замены раскрывается по кнопке: иначе недавние и похожие растянули бы
  // редактор, куда чаще заходят поправить подходы, а не менять упражнение.
  const [replacing, setReplacing] = useState(false);
  const body = useSheetDrag(onClose);
  // Характеристики своего упражнения правятся той же формой, что и при создании.
  const [editingTraits, setEditingTraits] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const min = num(repsMin);
      const max = num(repsMax);
      const drops = kind === "drop" ? parseSequence(sequence) : [];
      if (kind === "drop" && drops.length === 0) {
        throw new Error("Впишите повторы по сбросам, например 12-8-8");
      }
      await onSave({
        exercise_id: exercise.id,
        target_sets: num(sets),
        target_reps_min: kind === "drop" ? null : min,
        // Пустой верх диапазона значит фиксированное число повторов,
        // а не «сколько получится».
        target_reps_max: kind === "drop" ? null : (max ?? min),
        // Смена типа обязана менять суть, а не только подпись: без очистки
        // последовательности упражнение осталось бы дропсетом и в списке,
        // и на экране тренировки.
        rep_sequence: kind === "drop" ? drops : null,
        target_spec: specFor(kind, amount),
        rest_sec: num(rest),
      });
      notify("success");
      onClose();
    } catch (e) {
      notify("error");
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    try {
      await onDelete();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="sheet" role="dialog" aria-label="Правка упражнения">
      <div className="sheet__backdrop" onClick={onClose} />
      <div className="sheet__body" ref={body}>
        <div className="sheet__grip" />
        <p className="sheet__title">{exercise.name_ru}</p>

        <div className="stack">
          <label className="field">
            <span className="field__label">Подходы</span>
            <span className="field__control">
              <input
                className="field__input"
                inputMode="numeric"
                value={sets}
                onChange={(e) => setSets(e.target.value)}
              />
            </span>
          </label>

          {kind === "drop" ? (
            <label className="field">
              <span className="field__label">Повторы по сбросам</span>
              <span className="field__control">
                <input
                  className="field__input field__input--wide"
                  inputMode="numeric"
                  placeholder="12-8-8"
                  value={sequence}
                  onChange={(e) => setSequence(e.target.value)}
                />
              </span>
            </label>
          ) : (
            <>
          <label className="field">
            <span className="field__label">Повторы от</span>
            <span className="field__control">
              <input
                className="field__input"
                inputMode="numeric"
                value={repsMin}
                onChange={(e) => setRepsMin(e.target.value)}
              />
            </span>
          </label>

          <label className="field">
            <span className="field__label">Повторы до</span>
            <span className="field__control">
              <input
                className="field__input"
                inputMode="numeric"
                placeholder="—"
                value={repsMax}
                onChange={(e) => setRepsMax(e.target.value)}
              />
            </span>
          </label>

            </>
          )}

          <label className="field">
            <span className="field__label">Отдых</span>
            <span className="field__control">
              <input
                className="field__input"
                inputMode="numeric"
                value={rest}
                onChange={(e) => setRest(e.target.value)}
              />
              <span className="field__unit">сек</span>
            </span>
          </label>
        </div>

        <p className="card__title" style={{ margin: "4px 0 0" }}>
          Нагрузка
        </p>
        <div className="chips chips--wrap">
          {TARGET_KINDS.map((t) => (
            <button
              key={t.key}
              className={`chip ${kind === t.key ? "chip--active" : ""}`}
              onClick={() => {
                haptic();
                setKind(t.key);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {(kind === "percent_1rm" || kind === "rpe") && (
          <label className="field">
            <span className="field__label">
              {kind === "percent_1rm" ? "Процент от 1ПМ" : "Целевой RPE"}
            </span>
            <span className="field__control">
              <input
                className="field__input"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <span className="field__unit">{kind === "percent_1rm" ? "%" : ""}</span>
            </span>
          </label>
        )}

        <p className="card__title" style={{ margin: "4px 0 0" }}>
          Характеристики
        </p>
        {exercise.is_custom ? (
          editingTraits ? (
            <ExerciseForm
              initial={exercise}
              onSaved={(next) => {
                setExercise(next);
                setEditingTraits(false);
              }}
              onCancel={() => setEditingTraits(false)}
            />
          ) : (
            <>
              <p className="lead" style={{ margin: 0 }}>
                {muscleLabel(exercise.primary_muscle)} · {equipmentLabel(exercise.equipment)} ·{" "}
                {movementLabel(exercise.movement)}
              </p>
              <button className="link-button" onClick={() => setEditingTraits(true)}>
                Изменить характеристики
              </button>
            </>
          )
        ) : (
          <p className="lead" style={{ margin: 0 }}>
            {muscleLabel(exercise.primary_muscle)} — упражнение из общей библиотеки.
            Категорию там менять нельзя: это задело бы чужие программы. Заведите
            своё, если нужна другая группа.
          </p>
        )}

        <p className="card__title" style={{ margin: "4px 0 0" }}>
          Заменить упражнение
        </p>
        {replacing ? (
          <ExerciseSearch
            similarTo={exercise.id}
            exclude={[exercise.id]}
            excludedHint="Это то же самое упражнение."
            onPick={(next) => {
              setExercise(next);
              setReplacing(false);
            }}
          />
        ) : (
          <button className="link-button" onClick={() => setReplacing(true)}>
            Выбрать другое
          </button>
        )}

        {error && <p className="lead lead--error">{error}</p>}

        <button className="button button--wide" disabled={busy} onClick={() => void save()}>
          {busy ? "Сохраняю…" : "Сохранить"}
        </button>
        <button className="danger-button" disabled={busy} onClick={() => void remove()}>
          Убрать из тренировки
        </button>
      </div>
    </div>
  );
}
